/**
 * PSV-1890 — centered death-recap overlay (death-loop UX).
 *
 * Extends the PSV-1336 death flow: ArenaController supplies the per-life
 * stats (killer, survival time, kills, gem tallies, personal-best flags)
 * and drives visibility from the server broadcasts — `ShipKilled` →
 * `show(stats)`, `ShipRespawned` → `hide()`. The PLAY AGAIN button fires
 * `onPlayAgain()`, which the controller turns into a `RespawnNow` RPC;
 * the server validates it and the confirming `ShipRespawned` is what
 * actually dismisses the panel (the controller greys the button out via
 * `setPlayAgainEnabled(false)` while the RPC is in flight).
 *
 * Interaction uses the container-level `setInteractive` pattern from
 * UpgradePanel (PSV-1338 Bug 1 Pass 3): one hit rect over the whole card,
 * pointerdown maps container-local X/Y to the two button sub-rects, and
 * `stopPropagation()` always fires so a click anywhere on the card never
 * discharges the ship's weapon.
 *
 * Visibility note: hide/show park the container off-screen via
 * `setPosition` instead of `setVisible` — scrollFactor(0) makes the
 * container's x/y pure screen coords, so parking is equivalent, and it
 * keeps the panel exercisable under tests/mocks/phaser.mock.ts (whose
 * MockContainer deliberately has no setVisible/setAlpha).
 */

import Phaser from 'phaser';

const CARD_WIDTH = 380;
const CARD_HEIGHT = 300;
const CARD_RADIUS = 8;
// ABOVE the EffectsManager YOU-DIED overlay (~25).
const CARD_DEPTH = 30;

// Off-screen parking spot used by hide() (see visibility note above).
const HIDDEN_X = -10_000;
const HIDDEN_Y = -10_000;

const PAD_X = 40;
const CENTER_X = CARD_WIDTH / 2;

// Vertical layout (container-local coords).
const HEADER_Y = 24;
const KILLER_Y = 48;
const ROWS_TOP = 74;
const ROW_HEIGHT = 22;
const BEST_Y = 168;

// PSV-1890 — button hit rects in CONTAINER-LOCAL coords (UpgradePanel
// Bug 1 Pass 3 pattern). Container-level setInteractive intercepts every
// click on the card; the pointerdown handler then checks whether the
// local-space pointer landed inside these sub-rects. Keep in sync with
// the visual paint in repaintButtons().
const PLAY_BTN_X = (CARD_WIDTH - 300) / 2; // 40
const PLAY_BTN_Y = 192;
const PLAY_BTN_WIDTH = 300;
const PLAY_BTN_HEIGHT = 44;
const PLAY_BTN_RADIUS = 6;

const NAME_BTN_WIDTH = 120;
const NAME_BTN_HEIGHT = 24;
const NAME_BTN_X = CENTER_X - NAME_BTN_WIDTH / 2; // 130
const NAME_BTN_Y = 246;
const NAME_TEXT_Y = NAME_BTN_Y + NAME_BTN_HEIGHT / 2; // 258
const NAME_UNDERLINE_HALF_WIDTH = 40;
const NAME_UNDERLINE_Y = NAME_TEXT_Y + 9;

// Palette — matches StatPanel's rounded-card look.
const CARD_BG_COLOR = 0x0b101c;
const CARD_BG_ALPHA = 0.92;
const CARD_BORDER_COLOR = 0x2c3a5c;

const COLOR_ENABLED = 0x44ff7a;
const COLOR_DISABLED = 0x3a3a3a;
const UNDERLINE_COLOR = 0x7fd6ea;

const COLOR_HEADER = '#ff5577';
const COLOR_TEXT = '#eeeeee';
const COLOR_LABEL_DIM = '#9aa0b4';
const COLOR_VALUE = '#eaf6ff';
const COLOR_GOLD = '#ffd84a';
const COLOR_RED = '#ff5577';
const COLOR_NAME_LINK = '#7fd6ea';
const COLOR_BTN_LABEL_DARK = '#0b101c';
const COLOR_BTN_LABEL_DISABLED = '#888888';

const BEST_PULSE_MS = 600;
const BEST_PULSE_MIN_ALPHA = 0.35;

export interface DeathRecapStats {
  killerName: string;
  survivalSec: number;
  kills: number;
  gemsCollected: number;
  gemsLost: number;
  newBestCargo: boolean;
  newBestKills: boolean;
  /** PSV-1886 — extra bounty burst dropped on top of the cargo. */
  bountyDropped: number;
  /** PSV-1886 — streak that just ended (0/1 = nothing noteworthy). */
  streakEnded: number;
}

export interface DeathRecapCallbacks {
  onPlayAgain: () => void;
  onChangeName: () => void;
}

/** Mutable value texts — built once on first show(), updated per death. */
interface ValueRefs {
  killerText: Phaser.GameObjects.Text;
  survivedValue: Phaser.GameObjects.Text;
  killsValue: Phaser.GameObjects.Text;
  minedValue: Phaser.GameObjects.Text;
  lostValue: Phaser.GameObjects.Text;
  bestText: Phaser.GameObjects.Text;
  playLabel: Phaser.GameObjects.Text;
}

export class DeathRecapPanel {
  private readonly scene: Phaser.Scene;
  private readonly callbacks: DeathRecapCallbacks;

  private container: Phaser.GameObjects.Container | null = null;
  private buttonBg: Phaser.GameObjects.Graphics | null = null;
  private refs: ValueRefs | null = null;
  private bestPulseTween: Phaser.Tweens.Tween | null = null;

  private visible = false;
  /**
   * Whether PLAY AGAIN currently accepts clicks. The controller flips it
   * off while a RespawnNow RPC is in flight. We DON'T toggle the
   * container's interactive flag — the card must keep swallowing clicks
   * (stopPropagation) even while the button is greyed out.
   */
  private playAgainEnabled = true;
  private destroyed = false;

  constructor(scene: Phaser.Scene, callbacks: DeathRecapCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
  }

  /** Update the fixed text pool from this life's stats and reveal the card. */
  show(stats: DeathRecapStats): void {
    if (this.destroyed) return;
    this.ensureBuilt();
    const refs = this.refs;
    if (this.container === null || refs === null) return;

    // PSV-1886 — a dead streak is worth a eulogy on the killer line.
    const streakNote =
      stats.streakEnded >= 2 ? ` — your ${stats.streakEnded}-kill streak ends` : '';
    refs.killerText.setText(
      (stats.killerName === 'asteroid'
        ? 'asteroid collision'
        : `by ${stats.killerName}`) + streakNote,
    );

    refs.survivedValue.setText(formatDuration(stats.survivalSec));
    refs.killsValue.setText(String(stats.kills));
    refs.minedValue.setText(`◆ ${stats.gemsCollected}`);
    // PSV-1886 — the bounty burst rides the same row: total spilled loot.
    const totalLost = stats.gemsLost + stats.bountyDropped;
    if (totalLost > 0) {
      refs.lostValue.setText(
        stats.bountyDropped > 0
          ? `◆ ${stats.gemsLost} +${stats.bountyDropped} bounty`
          : `◆ ${stats.gemsLost}`,
      );
      refs.lostValue.setColor(COLOR_RED);
    } else {
      refs.lostValue.setText('—');
      refs.lostValue.setColor(COLOR_LABEL_DIM);
    }

    // Personal-best line — gold + alpha-pulse tween while shown, blanked
    // (and tween removed) otherwise. Rebuilt per show() so a best on one
    // life doesn't keep pulsing on the next.
    this.stopBestPulse();
    refs.bestText.setAlpha(1);
    if (stats.newBestCargo || stats.newBestKills) {
      refs.bestText.setText('NEW PERSONAL BEST!');
      this.bestPulseTween = this.scene.tweens.add({
        targets: refs.bestText,
        alpha: BEST_PULSE_MIN_ALPHA,
        duration: BEST_PULSE_MS,
        yoyo: true,
        repeat: -1,
      });
    } else {
      refs.bestText.setText('');
    }

    this.repaintButtons();

    // Reveal — screen-centered (scrollFactor 0 → x/y are screen coords).
    const cam = this.scene.cameras.main;
    this.container.setPosition(
      cam.centerX - CARD_WIDTH / 2,
      cam.centerY - CARD_HEIGHT / 2,
    );
    this.visible = true;
  }

  /** Dismissed by the controller on the server's ShipRespawned broadcast. */
  hide(): void {
    if (this.destroyed || this.container === null) return;
    this.stopBestPulse();
    this.container.setPosition(HIDDEN_X, HIDDEN_Y);
    this.visible = false;
  }

  /**
   * Grey the PLAY AGAIN button out + ignore its clicks — the controller
   * calls this with `false` while a RespawnNow RPC is in flight and
   * re-enables on rejection (success hides the whole panel instead).
   */
  setPlayAgainEnabled(enabled: boolean): void {
    if (this.destroyed || this.playAgainEnabled === enabled) return;
    this.playAgainEnabled = enabled;
    this.repaintButtons();
  }

  /**
   * Returns true if the given pointer screen position falls inside the
   * visible card. Same defensive hook as UpgradePanel/StatPanel expose
   * for ArenaController.handleFireInput — scrollFactor(0) means the
   * container x/y are screen coords, so compare against pointer.x/y
   * directly (NOT worldX/worldY). Always false while hidden.
   */
  containsPointer(pointer: { x: number; y: number }): boolean {
    if (this.destroyed || !this.visible || this.container === null) {
      return false;
    }
    const px = pointer.x;
    const py = pointer.y;
    const x0 = this.container.x;
    const y0 = this.container.y;
    return (
      px >= x0 &&
      px <= x0 + CARD_WIDTH &&
      py >= y0 &&
      py <= y0 + CARD_HEIGHT
    );
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.stopBestPulse();
    this.container?.destroy(true);
    this.container = null;
    this.buttonBg = null;
    this.refs = null;
    this.visible = false;
  }

  // ---------- private ----------

  /** Build the fixed pool of objects once; later show() calls only update. */
  private ensureBuilt(): void {
    if (this.container !== null) return;

    // Built parked off-screen; show() positions it at screen center.
    this.container = this.scene.add
      .container(HIDDEN_X, HIDDEN_Y)
      .setScrollFactor(0)
      .setDepth(CARD_DEPTH);

    // Background — StatPanel's rounded-card look.
    const background = this.scene.add.graphics();
    background.fillStyle(CARD_BG_COLOR, CARD_BG_ALPHA);
    background.fillRoundedRect(0, 0, CARD_WIDTH, CARD_HEIGHT, CARD_RADIUS);
    background.lineStyle(1, CARD_BORDER_COLOR, 1);
    background.strokeRoundedRect(0, 0, CARD_WIDTH, CARD_HEIGHT, CARD_RADIUS);
    this.container.add(background);

    const header = this.scene.add
      .text(CENTER_X, HEADER_Y, 'SHIP DESTROYED', {
        fontSize: '20px',
        color: COLOR_HEADER,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.container.add(header);

    const killerText = this.scene.add
      .text(CENTER_X, KILLER_Y, '', {
        fontSize: '14px',
        color: COLOR_TEXT,
      })
      .setOrigin(0.5);
    this.container.add(killerText);

    // Stats rows — static dim labels left, mutable values right-aligned.
    const survivedValue = this.addStatRow(0, 'Survived', COLOR_VALUE);
    const killsValue = this.addStatRow(1, 'Kills', COLOR_VALUE);
    const minedValue = this.addStatRow(2, 'Crystals mined', COLOR_GOLD);
    const lostValue = this.addStatRow(3, 'Cargo lost', COLOR_LABEL_DIM);

    const bestText = this.scene.add
      .text(CENTER_X, BEST_Y, '', {
        fontSize: '13px',
        color: COLOR_GOLD,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.container.add(bestText);

    // Buttons — one Graphics for both; repaintButtons() drives the
    // enabled/disabled band + the change-name underline.
    this.buttonBg = this.scene.add.graphics();
    this.container.add(this.buttonBg);

    const playLabel = this.scene.add
      .text(CENTER_X, PLAY_BTN_Y + PLAY_BTN_HEIGHT / 2, 'PLAY AGAIN', {
        fontSize: '16px',
        color: COLOR_BTN_LABEL_DARK,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.container.add(playLabel);

    // Secondary text button — underlined-look via the lineBetween painted
    // in repaintButtons() (Phaser text styles have no native underline).
    const nameLabel = this.scene.add
      .text(CENTER_X, NAME_TEXT_Y, 'change name', {
        fontSize: '13px',
        color: COLOR_NAME_LINK,
      })
      .setOrigin(0.5);
    this.container.add(nameLabel);

    this.refs = {
      killerText,
      survivedValue,
      killsValue,
      minedValue,
      lostValue,
      bestText,
      playLabel,
    };

    // PSV-1338 Bug 1 (Pass 3) pattern — container-level interactive over
    // the full card rect. scrollFactor(0) doesn't propagate to children,
    // so per-button child hit areas would be computed in the wrong space;
    // instead Phaser hands the pointerdown handler localX/localY already
    // transformed through the container's matrix, and the button pick is
    // straight rect-contains math.
    this.container.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, CARD_WIDTH, CARD_HEIGHT),
      Phaser.Geom.Rectangle.Contains,
    );
    this.container.input!.cursor = 'pointer';
    this.container.on(
      'pointerdown',
      (
        _pointer: Phaser.Input.Pointer,
        localX: number,
        localY: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        // ALWAYS stop propagation — a click anywhere on the card must
        // never reach the scene-level fire handler and discharge the
        // ship's weapon (same rationale as UpgradePanel Bug 1).
        event.stopPropagation();
        if (this.destroyed || !this.visible) return;
        if (
          rectContains(
            localX,
            localY,
            PLAY_BTN_X,
            PLAY_BTN_Y,
            PLAY_BTN_WIDTH,
            PLAY_BTN_HEIGHT,
          )
        ) {
          if (!this.playAgainEnabled) return;
          this.callbacks.onPlayAgain();
          return;
        }
        if (
          rectContains(
            localX,
            localY,
            NAME_BTN_X,
            NAME_BTN_Y,
            NAME_BTN_WIDTH,
            NAME_BTN_HEIGHT,
          )
        ) {
          this.callbacks.onChangeName();
        }
      },
      this,
    );

    this.repaintButtons();
  }

  /** Add one label+value stats row; returns the mutable value text. */
  private addStatRow(
    index: number,
    label: string,
    valueColor: string,
  ): Phaser.GameObjects.Text {
    const rowY = ROWS_TOP + index * ROW_HEIGHT;
    const labelText = this.scene.add.text(PAD_X, rowY, label, {
      fontSize: '13px',
      color: COLOR_LABEL_DIM,
    });
    this.container!.add(labelText);

    const valueText = this.scene.add
      .text(CARD_WIDTH - PAD_X, rowY, '', {
        fontSize: '13px',
        color: valueColor,
      })
      .setOrigin(1, 0);
    this.container!.add(valueText);
    return valueText;
  }

  /** Repaint the PLAY AGAIN band + change-name underline for the current state. */
  private repaintButtons(): void {
    if (this.destroyed || this.buttonBg === null) return;

    this.buttonBg.clear();
    this.buttonBg.fillStyle(
      this.playAgainEnabled ? COLOR_ENABLED : COLOR_DISABLED,
      1,
    );
    this.buttonBg.fillRoundedRect(
      PLAY_BTN_X,
      PLAY_BTN_Y,
      PLAY_BTN_WIDTH,
      PLAY_BTN_HEIGHT,
      PLAY_BTN_RADIUS,
    );
    this.buttonBg.lineStyle(1, UNDERLINE_COLOR, 0.7);
    this.buttonBg.lineBetween(
      CENTER_X - NAME_UNDERLINE_HALF_WIDTH,
      NAME_UNDERLINE_Y,
      CENTER_X + NAME_UNDERLINE_HALF_WIDTH,
      NAME_UNDERLINE_Y,
    );

    this.refs?.playLabel.setColor(
      this.playAgainEnabled ? COLOR_BTN_LABEL_DARK : COLOR_BTN_LABEL_DISABLED,
    );
  }

  private stopBestPulse(): void {
    if (this.bestPulseTween === null) return;
    this.bestPulseTween.remove();
    this.bestPulseTween = null;
  }
}

/** Seconds → 'mm:ss' (floored, zero-padded seconds; minutes unbounded). */
function formatDuration(totalSec: number): string {
  const secs = Math.max(0, Math.floor(totalSec));
  const mm = Math.floor(secs / 60);
  const ss = secs % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

function rectContains(
  px: number,
  py: number,
  x: number,
  y: number,
  width: number,
  height: number,
): boolean {
  return px >= x && px <= x + width && py >= y && py <= y + height;
}
